
import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import SuccessSvg from "@/components/shared/svgs/success-svg";
import Icon from "@/components/icon";

type Props = {
  price?: number;
  courseName?: string;
  factorId?: string;
  createdAt?: string;
  miniAppUuid?: string;
  courseId?: number;
  purchaseSource?: string;
};

export default function SuccessfulPayment({
  factorId,
  courseName,
  createdAt,
  price,
  miniAppUuid,
  courseId,
  purchaseSource,
}: Props) {
  const invoiceRef = useRef<HTMLDivElement | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const queryClient = useQueryClient();

  // A successful purchase just granted access — invalidate the persisted caches
  // (they survive the gateway round-trip via PersistQueryClient, so without this
  // the course page shows stale is_purchased and the list stays stale ~1h).
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["vitrin-course"] });
    queryClient.invalidateQueries({ queryKey: ["vitrin-course-lessons"] });
    queryClient.invalidateQueries({ queryKey: ["my-courses"] });
  }, [queryClient]);
  
  const displayPrice =
    price && price > 0
      ? `${price.toLocaleString("fa-IR")} تومان`
      : price === 0
        ? "رایگان"
        : "";
  const courseHref =
    courseId && miniAppUuid
      ? `/course/${courseId}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`
      : "/my-courses";

  const handleShare = useCallback(async () => {
    try {
      setIsSharing(true);
      if (navigator.share) {
        await navigator.share({
          title: "فاکتور پرداخت",
          text: `فاکتور خرید ${courseName ?? "دوره"}`,
          url: window.location.href,
        });
        return;
      }

      await navigator.clipboard?.writeText(window.location.href);
    } finally {
      setIsSharing(false);
    }
  }, [courseName]);

  const handleSaveImage = useCallback(() => {
    setIsSavingImage(true);
    window.print();
    window.setTimeout(() => setIsSavingImage(false), 500);
  }, []);

  return (
    <div
      ref={invoiceRef}
      className="relative w-full max-w-md overflow-hidden rounded-2xl border border-default-100 bg-background shadow-lg shadow-default-900/5 print:mx-auto"
    >
      <div className="relative flex flex-col items-center gap-3 px-4 pt-6 pb-4">
        <SuccessSvg />
        <p className="text-base font-bold text-default-900">پرداخت موفق</p>
      </div>


      <div className="relative h-3">
        <span className="absolute -right-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <span className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 border-t border-dashed border-default-200" />
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5 pt-3">
        <p className="text-center text-sm leading-7 text-default-700">
          دوره{" "}
          <span className="font-bold text-default-900">
            {courseName || "آموزش"}
          </span>{" "}
          با موفقیت خریداری شد.
        </p>

        {/* Receipt rows: label on the right, value on the left, dotted leader
            in between for a classic invoice look. */}
        <div className="flex flex-col gap-6 rounded-xl bg-content3/40 p-4 text-xs">
          <ReceiptRow label="مبلغ پرداختی" value={displayPrice} />
          <ReceiptRow label="شماره پیگیری" value={factorId} mono />
          <ReceiptRow label="تاریخ پرداخت" value={createdAt} />
          <ReceiptRow label="خرید از طریق" value={purchaseSource} />
        </div>

        <div className="grid w-full grid-cols-2 gap-2 print:hidden">
          <button
            type="button"
            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-default-100 bg-background text-xs font-medium text-default-700 disabled:opacity-70"
            onClick={handleShare}
            disabled={isSharing || isSavingImage}
          >
            <Icon name="share" size={20} />
            {isSharing ? "در حال آماده‌سازی..." : "اشتراک گذاری"}
          </button>
          <button
            type="button"
            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-default-100 bg-background text-xs font-medium text-default-700 disabled:opacity-70"
            onClick={handleSaveImage}
            disabled={isSharing || isSavingImage}
          >
            <Icon name="download" size={20} />
            {isSavingImage ? "در حال آماده‌سازی..." : "ذخیره تصویر"}
          </button>
        </div>

        <Link
          replace={true}
          className="block w-full rounded-xl bg-primary p-3.5 text-center text-sm font-bold text-white shadow-md shadow-primary/30 print:hidden"
          to={courseHref}
        >
          مشاهده آموزش
        </Link>
      </div>
    </div>
  );
}

function ReceiptRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex w-full items-center gap-2">
      <span className="shrink-0 text-default-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-default-300" />
      <span
        className={`shrink-0 font-semibold text-default-800 ${mono ? "font-mono tracking-tight" : ""
          }`}
        dir={mono ? "ltr" : undefined}
      >
        {value || "-"}
      </span>
    </div>
  );
}
